import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductHero from './components/ProductHero';
import ProductFeatures from './components/ProductFeatures';
import ProductSpecs from './components/ProductSpecs';
import ProductCTA from './components/ProductCTA';
import { productData } from '../../mocks/products';

export default function ProductDetail() {
  const { model } = useParams<{ model: string }>();
  const navigate = useNavigate();
  const product = productData[model as keyof typeof productData];
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f3ef]">
        <div className="text-center">
          <div className="text-6xl mb-4">🪑</div>
          <h2 className="text-2xl font-bold text-[#1a2332] mb-4">製品が見つかりません</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-[#1a2332] text-white px-8 py-3 rounded-full text-sm font-bold hover:bg-[#2c4a6e] transition-all whitespace-nowrap cursor-pointer"
          >
            トップページへ戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="cursor-pointer">
            <img 
              src={scrolled 
                ? "https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/826c552476badc913a23e7605116bff7.png"
                : "https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/5bc5af811e2f931fbde5d199712dd10f.png"
              }
              alt="MeVER Chair ロゴ" 
              title="MeVER Chair"
              className="h-8 w-auto"
            />
          </a>
          <div className="flex items-center space-x-8">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="text-sm font-medium text-[#1a2332] hover:text-[#2c4a6e] transition-colors cursor-pointer whitespace-nowrap">
              トップページ
            </a>
            {(['core', 'pro', 'style'] as const).map((m) => (
              <a
                key={m}
                href={`/product/${m}`}
                onClick={(e) => { e.preventDefault(); navigate(`/product/${m}`); }}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${model === m ? 'font-bold border-b-2' : 'hover:text-[#2c4a6e]'}`}
                style={{ color: model === m ? productData[m].color : '#1a2332', borderColor: model === m ? productData[m].color : 'transparent' }}
              >
                {productData[m].name}
              </a>
            ))}
            <a
              href="/compare"
              onClick={(e) => { e.preventDefault(); navigate('/compare'); window.scrollTo(0, 0); }}
              className="text-sm font-medium text-[#1a2332] hover:text-[#2c4a6e] transition-colors cursor-pointer whitespace-nowrap"
            >
              製品比較
            </a>
          </div>
        </div>
      </nav>

      <ProductHero product={product} />
      <ProductFeatures product={product} />
      <ProductSpecs product={product} />
      <ProductCTA product={product} currentModel={model || 'core'} />

      {/* Footer */}
      <footer className="bg-[#3d6b4a] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-sm">
          <div className="mb-4 md:mb-0">
            <img 
              src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/826c552476badc913a23e7605116bff7.png" 
              alt="MeVER Chair ロゴ" 
              title="MeVER Chair"
              className="h-7 w-auto brightness-0 invert"
            />
          </div>
          <div className="flex items-center space-x-6 text-white/70">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="hover:text-white transition-colors cursor-pointer">トップページ</a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-white transition-colors cursor-pointer">Web Design</a>
            <span>© 2025 MeVER Chair</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
